<aside id="bid-details-sidebar" class="sidebar-shell h-full flex flex-col" [attr.aria-label]="'bids.sidebar.title' | translate">
  <header class="sidebar-header px-5 py-5">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div id="bid-details-sidebar-header-icon" class="header-icon-box">
        <mat-icon>gavel</mat-icon>
      </div>

      @if (data().isBestBid) {
        <span id="bid-details-sidebar-best-badge" class="best-badge">
          {{ 'bids.sidebar.best_bid_badge' | translate }}
        </span>
      }

      <button
        id="bid-details-sidebar-close-button"
        type="button"
        class="close-button"
        [attr.aria-label]="'common.close' | translate"
        (click)="onClose()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <h3 id="bid-details-sidebar-title" class="sidebar-title">{{ 'bids.sidebar.title' | translate }}</h3>
    <p id="bid-details-sidebar-carrier-name" class="sidebar-subtitle">{{ data().carrierName }}</p>
    <p id="bid-details-sidebar-meta" class="sidebar-meta">
      {{ 'bids.sidebar.meta' | translate:{ bidId: data().bidId, offerNumber: data().offerNumber } }}
    </p>
  </header>

  <section class="stats-grid grid grid-cols-3">
    <article id="bid-details-current-bid-stat" class="stat-card stat-card--green">
      <div class="stat-icon"><mat-icon>gavel</mat-icon></div>
      <strong class="stat-value">{{ data().bidValueFormatted }}</strong>
      <p class="stat-label">{{ 'bids.sidebar.stats.current_bid' | translate }}</p>
    </article>

    <article id="bid-details-first-bid-stat" class="stat-card stat-card--gray">
      <div class="stat-icon"><mat-icon>history</mat-icon></div>
      <strong class="stat-value">{{ data().firstBidValueFormatted }}</strong>
      <p class="stat-label">{{ 'bids.sidebar.stats.first_bid' | translate }}</p>
    </article>

    <article id="bid-details-evolution-stat" class="stat-card stat-card--teal">
      <div class="stat-icon"><mat-icon>trending_down</mat-icon></div>
      <strong class="stat-value">{{ data().evolutionText }}</strong>
      <p class="stat-label">{{ 'bids.sidebar.stats.evolution' | translate }}</p>
    </article>
  </section>

  <div class="flex-1 overflow-y-auto px-4 py-4 space-y-3">
    <section class="section-card section-card--dates">
      <button
        id="bid-details-dates-toggle"
        type="button"
        class="section-header w-full flex items-center justify-between"
        (click)="toggleDatesSection()"
      >
        <span class="flex items-center gap-2">
          <span class="section-icon-box section-icon-box--blue"><mat-icon>schedule</mat-icon></span>
          <strong>{{ 'bids.sidebar.sections.dates' | translate }}</strong>
        </span>
        <mat-icon [class.arrow-open]="isDatesSectionOpen()">expand_more</mat-icon>
      </button>

      @if (isDatesSectionOpen()) {
        <div class="section-body">
          @for (item of data().timeline; track trackById($index, item)) {
            <div class="timeline-row flex items-start gap-3" [id]="'bid-details-timeline-' + item.id">
              <span [class]="getTimelineIconClass(item.iconVariant)">
                <mat-icon>{{ item.icon }}</mat-icon>
              </span>
              <div class="min-w-0 flex-1">
                <p class="timeline-label">{{ item.labelKey | translate }}</p>
                <p class="timeline-value">{{ item.value }}</p>
                @if (item.hint) {
                  <p class="timeline-hint">{{ item.hint }}</p>
                }
              </div>
            </div>
          }
        </div>
      }
    </section>

    <section class="section-card">
      <button
        id="bid-details-observations-toggle"
        type="button"
        class="section-header w-full flex items-center justify-between"
        (click)="toggleObservationsSection()"
      >
        <span class="flex items-center gap-2">
          <span class="section-icon-box section-icon-box--teal"><mat-icon>comment</mat-icon></span>
          <strong>{{ 'bids.sidebar.sections.observations' | translate }}</strong>
        </span>
        <mat-icon [class.arrow-open]="isObservationsSectionOpen()">expand_more</mat-icon>
      </button>

      @if (isObservationsSectionOpen()) {
        <div class="section-body">
          <p id="bid-details-observations-text" class="observations-text">
            {{ data().observations || ('bids.sidebar.observations.empty' | translate) }}
          </p>
        </div>
      }
    </section>

    <section class="section-card">
      <button
        id="bid-details-carrier-toggle"
        type="button"
        class="section-header w-full flex items-center justify-between"
        (click)="toggleCarrierSection()"
      >
        <span class="flex items-center gap-2">
          <span class="section-icon-box section-icon-box--teal"><mat-icon>business</mat-icon></span>
          <strong>{{ 'bids.sidebar.sections.carrier_info' | translate }}</strong>
        </span>
        <mat-icon [class.arrow-open]="isCarrierSectionOpen()">expand_more</mat-icon>
      </button>

      @if (isCarrierSectionOpen()) {
        <div class="section-body">
          @for (item of data().carrierInfo; track trackById($index, item)) {
            <div class="carrier-info-row flex items-start gap-3" [id]="'bid-details-carrier-info-' + item.id">
              <span [class]="getCarrierInfoIconClass(item.iconVariant)">
                <mat-icon>{{ item.icon }}</mat-icon>
              </span>
              <div class="min-w-0 flex-1">
                <p class="timeline-label">{{ item.labelKey | translate }}</p>
                <p class="timeline-value">{{ item.value }}</p>
              </div>
              @if (item.id === 'phone') {
                <button
                  type="button"
                  class="action-btn action-btn-whatsapp shrink-0"
                  id="bid-details-whatsapp-button"
                  (click)="onWhatsapp()"
                  [appTippy]="'invitations.actions.whatsapp' | translate"
                  [tippyOptions]="{ placement: 'top' }"
                >
                  <mat-icon svgIcon="whatsapp"></mat-icon>
                </button>
              }
            </div>
          }
        </div>
      }
    </section>

    <section class="section-card">
      <button
        id="bid-details-chat-toggle"
        type="button"
        class="section-header w-full flex items-center justify-between"
        (click)="toggleChatSection()"
      >
        <span class="flex items-center gap-2">
          <span class="section-icon-box section-icon-box--teal"><mat-icon>chat_bubble_outline</mat-icon></span>
          <strong>{{ 'bids.sidebar.sections.chat' | translate:{ carrierName: data().carrierName } }}</strong>
        </span>

        <span class="flex items-center gap-2">
          @if (data().unreadMessages > 0) {
            <span id="bid-details-chat-unread-badge" class="chat-unread-badge">{{ chatBadgeValue() }}</span>
          }
          <mat-icon [class.arrow-open]="isChatSectionOpen()">expand_more</mat-icon>
        </span>
      </button>

      @if (isChatSectionOpen()) {
        <div class="section-body section-body--chat">
          <app-offer-chat
            [bidId]="data().chatBidId ?? data().bidId"
            [companyId]="data().chatDestinationClientId"
            [autoFocusInput]="data().openChatOnOpen ?? false"
          />
        </div>
      }
    </section>
  </div>

  <footer class="sidebar-footer px-4 pb-4 flex flex-col gap-3">
    <button
      id="bid-details-approve-button"
      type="button"
      class="btn-primary w-full justify-center"
      (click)="onApproveBid()"
      [disabled]="!canApproveBid()"
    >
      <mat-icon>check_circle</mat-icon>
      {{ 'bids.sidebar.actions.approve' | translate }}
    </button>

    <button
      id="bid-details-negotiate-button"
      type="button"
      class="btn-secondary w-full justify-center"
      (click)="onNegotiate()"
    >
      <mat-icon>handshake</mat-icon>
      {{ 'bids.sidebar.actions.negotiate' | translate }}
    </button>
  </footer>
</aside>
